import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, Calendar, Clock, FileText, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { leaveAPI, establishmentAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';

const LEAVE_TYPE_LABELS = {
  annual: 'Annual Leave', sick: 'Sick Leave', casual: 'Casual Leave',
  maternity: 'Maternity Leave', paternity: 'Paternity Leave',
  emergency: 'Emergency Leave', bereavement: 'Bereavement Leave',
  study: 'Study Leave', unpaid: 'Unpaid Leave', other: 'Other',
};

const fmtDate = (d) => {
  if (!d) return '—';
  // YYYY-MM-DD strings must be formatted directly — passing them to
  // new Date() treats them as UTC midnight, which shifts to the previous
  // day in any browser timezone west of UTC.
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  }
  return new Date(d).toLocaleDateString('en-IN');
};

const ApproveLeave = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();
  const [establishments, setEstablishments]     = useState([]);
  const [selectedEst, setSelectedEst]           = useState(lockedEstId || '');
  const [leaves, setLeaves]                     = useState([]);
  const [loading, setLoading]                   = useState(false);
  const [estLoading, setEstLoading]             = useState(true);
  const [error, setError]                       = useState('');
  const [processingId, setProcessingId]         = useState(null);
  const [rejectInputs, setRejectInputs]         = useState({});   // leaveId → reason text

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEst(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) { setEstLoading(false); return; }
    establishmentAPI.getAll()
      .then(r => setEstablishments(r.establishments || []))
      .catch(() => setEstablishments([]))
      .finally(() => setEstLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await leaveAPI.getPending();
      const all = response.pending_leaves || [];
      const filtered = selectedEst
        ? all.filter(l => String(l.establishment_id) === String(selectedEst))
        : all;
      setLeaves(filtered);
    } catch (err) {
      setError('Could not load leave applications. Please try again.');
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEst]);

  useEffect(() => {
    if (selectedEst) fetchLeaves();
    else setLeaves([]);
  }, [selectedEst, fetchLeaves]);

  const handleApprove = async (leaveId) => {
    setProcessingId(leaveId);
    setError('');
    try {
      await leaveAPI.approve(leaveId, {
        status: 'approved',
        approvedBy: user?.username || user?.email || 'Manager',
        comments: '',
      });
      window.alert('Leave approved.');
      fetchLeaves();
    } catch (err) {
      setError('Failed to approve: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (leaveId) => {
    const reason = rejectInputs[leaveId]?.trim();
    if (!reason) {
      setRejectInputs(prev => ({ ...prev, [`${leaveId}_touched`]: true }));
      return;
    }
    setProcessingId(leaveId);
    setError('');
    try {
      await leaveAPI.approve(leaveId, {
        status: 'rejected',
        approvedBy: user?.username || user?.email || 'Manager',
        comments: reason,
      });
      setRejectInputs(prev => {
        const next = { ...prev };
        delete next[leaveId];
        delete next[`${leaveId}_touched`];
        return next;
      });
      window.alert('Leave rejected.');
      fetchLeaves();
    } catch (err) {
      setError('Failed to reject: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        <div className="update-employee-header">
          <h2 className="update-employee-title">Approve Leave Applications</h2>
          <p className="update-employee-subtitle">Review and approve or reject employee leave requests</p>
        </div>

        {!lockedEstId && (
          <EstablishmentCard
            establishments={establishments}
            value={selectedEst}
            onChange={e => setSelectedEst(e.target.value)}
            loading={estLoading}
          />
        )}

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {selectedEst && (
          <div className="update-employee-form-card animate-fadeIn">
            <div className="update-employee-section-header">
              <h3 className="update-employee-section-title">
                Pending Leave Applications
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {!loading && <span className="update-employee-employee-badge">{leaves.length} pending</span>}
                <button
                  onClick={fetchLeaves}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.8rem', background: 'var(--theme-accent, #eef2ff)', border: '1px solid var(--theme-border, #e2e8f0)', borderRadius: '0.375rem', color: 'var(--theme-primary, #6366f1)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  <RefreshCw size={13} className={loading ? 'al-spinner' : ''} />
                  Refresh
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                <Loader size={32} style={{ margin: '0 auto 0.75rem', display: 'block', animation: 'spin 1s linear infinite' }} />
                <p style={{ margin: 0 }}>Loading applications…</p>
              </div>
            ) : leaves.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                <Calendar size={40} style={{ margin: '0 auto 0.75rem', display: 'block', opacity: 0.4 }} />
                <p style={{ margin: 0, fontWeight: 500 }}>No pending approvals</p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', opacity: 0.7 }}>All leave requests have been reviewed.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {leaves.map((leave) => {
                  const isProcessing = processingId === leave.leave_id;
                  const showRejectReason = rejectInputs[`${leave.leave_id}_touched`] && !rejectInputs[leave.leave_id]?.trim();
                  return (
                    <div key={leave.leave_id} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.25rem', background: 'var(--theme-surface, #ffffff)' }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div>
                          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
                            {leave.employee_name || leave.employeeName} &nbsp;
                            <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 400 }}>({leave.employee_id})</span>
                          </h4>
                          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.8rem' }}>
                            {leave.department_id && <>{leave.department_id} · </>}
                            Applied: {fmtDate(leave.applied_date)}
                            {leave.leave_request_number && <> · {leave.leave_request_number}</>}
                          </p>
                        </div>
                        <span style={{ padding: '0.2rem 0.65rem', borderRadius: '1rem', fontSize: '0.72rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap' }}>
                          Pending
                        </span>
                      </div>

                      {/* Details grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Leave Type</div>
                          <div style={{ fontSize: '0.9rem', color: '#374151', marginTop: '0.15rem' }}>
                            {LEAVE_TYPE_LABELS[leave.leave_type] || leave.leave_type}
                            {leave.half_day ? ' (Half Day)' : ''}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Duration</div>
                          <div style={{ fontSize: '0.9rem', color: '#374151', marginTop: '0.15rem' }}>
                            {fmtDate(leave.start_date)} → {fmtDate(leave.end_date)}
                            <span style={{ marginLeft: '0.4rem', color: '#6366f1', fontWeight: 600 }}>({leave.days_count}d)</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Reason</div>
                          <div style={{ fontSize: '0.9rem', color: '#374151', marginTop: '0.15rem' }}>{leave.reason}</div>
                        </div>
                      </div>

                      {leave.remarks && (
                        <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
                          <strong>Remarks:</strong> {leave.remarks}
                        </div>
                      )}

                      {/* Rejection reason input */}
                      <div style={{ marginBottom: '0.75rem' }}>
                        <input
                          type="text"
                          placeholder="Rejection reason (required to reject)"
                          value={rejectInputs[leave.leave_id] || ''}
                          onChange={e => setRejectInputs(prev => ({ ...prev, [leave.leave_id]: e.target.value }))}
                          style={{
                            width: '100%', padding: '0.45rem 0.75rem', fontSize: '0.85rem',
                            border: `1px solid ${showRejectReason ? '#fca5a5' : '#e5e7eb'}`,
                            borderRadius: '0.375rem', outline: 'none', boxSizing: 'border-box',
                            background: showRejectReason ? '#fef2f2' : 'var(--theme-surface, #ffffff)',
                          }}
                        />
                        {showRejectReason && <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.2rem' }}>Please enter a reason before rejecting.</div>}
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleApprove(leave.leave_id)}
                          disabled={isProcessing}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 1rem', background: '#10b981', color: '#fff', border: 'none', borderRadius: '0.375rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: isProcessing ? 0.7 : 1 }}
                        >
                          {isProcessing ? <Loader size={14} /> : <Check size={14} />}
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(leave.leave_id)}
                          disabled={isProcessing}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '0.375rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: isProcessing ? 0.7 : 1 }}
                        >
                          {isProcessing ? <Loader size={14} /> : <X size={14} />}
                          Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {selectedEst && !loading && (
          <div className="update-employee-info-box animate-fadeIn">
            <div className="update-employee-info-content">
              <Clock size={18} className="update-employee-info-icon" />
              <div className="update-employee-info-text">
                <strong>Tip:</strong> Enter a rejection reason before clicking Reject. Approved leaves automatically update the employee's leave balance.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproveLeave;
